import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Gift, Percent, Zap, Sparkles, Shield, Clock, Check, TrendingUp } from 'lucide-react';

const ICON_MAP = {
  percent: Percent,
  zap: Zap,
  sparkles: Sparkles,
  shield: Shield,
};

function timeLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3600000);
  if (h >= 24) return `${Math.floor(h / 24)}d left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.floor(ms / 60000)}m left`;
}

export default function RewardsStore() {
  const [catalog, setCatalog] = useState([]);
  const [balance, setBalance] = useState(0);
  const [mine, setMine] = useState([]);
  const [redeeming, setRedeeming] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [seasonActive, setSeasonActive] = useState(false);
  const [seasonExpiry, setSeasonExpiry] = useState(null);
  const [spCost, setSpCost] = useState(1000);
  const [spDays, setSpDays] = useState(30);

  const load = async () => {
    try {
      const [c, m, b] = await Promise.all([
        api.get('/referral_rewards/catalog'),
        api.get('/referral_rewards/mine'),
        api.get('/referral_rewards/bundles/catalog'),
      ]);
      setCatalog(c.data.catalog || []);
      setBalance(c.data.balance_points || 0);
      setMine(m.data.perks || []);
      setBundles(b.data.bundles || []);
      setSeasonActive(b.data.season_pass_active || false);
      setSpCost(b.data.season_pass_cost || 1000);
      setSpDays(b.data.season_pass_days || 30);
      const sp = (m.data.active || []).find(p => p.kind === 'season_pass');
      setSeasonExpiry(sp?.expires_at || null);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const redeemBundle = async (bid) => {
    setRedeeming('bundle_' + bid);
    try {
      const r = await api.post('/referral_rewards/bundles/redeem', { bundle_id: bid });
      toast.success(`Bundle redeemed! ${r.data.perks.length} perks · ${r.data.balance_points.toFixed(0)} pts left`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setRedeeming(null);
  };

  const redeemSeasonPass = async () => {
    setRedeeming('season_pass');
    try {
      const r = await api.post('/referral_rewards/season_pass/redeem');
      toast.success(`🎫 Season Pass active for ${spDays} days · ${r.data.balance_points.toFixed(0)} pts left`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setRedeeming(null);
  };

  const redeem = async (rid) => {
    setRedeeming(rid);
    try {
      const r = await api.post('/referral_rewards/redeem', { reward_id: rid });
      const tradable = ['nft_booster', 'battle_insurance'].includes(r.data.perk?.kind);
      if (tradable) {
        const perkId = r.data.perk.perk_id;
        const cost = r.data.perk.cost_points;
        const suggestedAsk = Math.round(cost * 1.25);  // suggest 25% markup
        toast.success(`Redeemed! · ${r.data.balance_points.toFixed(0)} pts left`, {
          description: `Not planning to use it? List it in the marketplace for ${suggestedAsk} pts.`,
          duration: 8000,
          action: {
            label: `List @ ${suggestedAsk}p`,
            onClick: async () => {
              try {
                await api.post('/referral_rewards/marketplace/list', { perk_id: perkId, ask_points: suggestedAsk });
                toast.success(`Listed for ${suggestedAsk} pts — visit the marketplace below`);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'List failed');
              }
            },
          },
        });
      } else {
        toast.success(`Redeemed! · ${r.data.balance_points.toFixed(0)} pts left`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setRedeeming(null);
  };

  const activePerks = mine.filter(p => !p.consumed && (!p.expires_at || new Date(p.expires_at) > new Date()));

  // Cross-sell: recommend a complementary perk based on what's active
  const CROSS_SELL_MAP = {
    fee_discount_50: 'battle_insurance',
    fee_discount_100: 'battle_insurance',
    battle_insurance: 'nft_booster',
    nft_booster: 'fee_discount_50',
  };
  const suggestion = (() => {
    const activeRewardIds = new Set(activePerks.map(p => p.reward_id));
    for (const rid of activeRewardIds) {
      const rec = CROSS_SELL_MAP[rid];
      if (!rec || activeRewardIds.has(rec)) continue;
      const target = catalog.find(c => c.reward_id === rec);
      const anchor = catalog.find(c => c.reward_id === rid);
      if (target && anchor) return { anchor, target };
    }
    return null;
  })();

  return (
    <section className="border border-[#2B3139] bg-[#181A20] p-5" data-testid="rewards-store">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-[#FCD535]" />
          <h2 className="text-white font-semibold text-lg">Referral Rewards Store</h2>
        </div>
        <div className="bg-[#FCD535]/10 border border-[#FCD535]/40 px-3 py-1.5 flex items-center gap-2" data-testid="points-balance">
          <span className="text-xs uppercase text-[#B7BDC6] font-mono">Balance</span>
          <span className="text-[#FCD535] font-mono font-semibold text-lg">{balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} pts</span>
        </div>
      </div>

      {activePerks.length > 0 && (
        <div className="mb-4 border border-[#0ECB81]/30 bg-[#0ECB81]/8 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Check className="w-3.5 h-3.5 text-[#0ECB81]" />
            <span className="text-xs uppercase font-mono text-[#0ECB81]">Active Perks · {activePerks.length}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {activePerks.map(p => (
              <div key={p.perk_id} className="text-xs bg-[#0B0E11] border border-[#2B3139] px-2.5 py-1.5 flex items-center gap-2" data-testid={`active-perk-${p.reward_id}`}>
                <span className="text-white font-semibold">{p.name}</span>
                {p.expires_at && (
                  <span className="text-[10px] font-mono text-[#848E9C] flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {timeLeft(p.expires_at)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {suggestion && (
        <div className="mb-4 border border-[#B26BFF]/30 bg-gradient-to-r from-[#B26BFF]/10 to-transparent p-3 flex items-center gap-3" data-testid="cross-sell-banner">
          <TrendingUp className="w-4 h-4 text-[#B26BFF] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase font-mono text-[#B26BFF]">Complete the set</div>
            <div className="text-sm text-white truncate">
              You have <span className="font-semibold">{suggestion.anchor.name}</span> — pair it with <span className="font-semibold">{suggestion.target.name}</span> for {suggestion.target.cost_points.toFixed(0)} pts.
            </div>
          </div>
          <Button
            onClick={() => redeem(suggestion.target.reward_id)}
            disabled={balance < suggestion.target.cost_points || redeeming === suggestion.target.reward_id}
            className="h-8 px-3 text-xs bg-[#B26BFF] hover:bg-[#9B4EE0] text-white shrink-0"
            data-testid="cross-sell-redeem-btn">
            {balance >= suggestion.target.cost_points ? `Redeem · ${suggestion.target.cost_points.toFixed(0)}p` : `Need ${(suggestion.target.cost_points - balance).toFixed(0)}p more`}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {catalog.map(r => {
          const Icon = ICON_MAP[r.icon] || Gift;
          const affordable = balance >= r.cost_points;
          const isActive = activePerks.some(p => p.reward_id === r.reward_id);
          return (
            <div key={r.reward_id} className={`border p-4 flex flex-col gap-2 ${isActive ? 'border-[#0ECB81]/40 bg-[#0ECB81]/5' : affordable ? 'border-[#2B3139] bg-[#0B0E11] hover:border-[#FCD535]' : 'border-[#2B3139] bg-[#0B0E11] opacity-60'} transition-colors`} data-testid={`reward-card-${r.reward_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 grid place-items-center bg-[#FCD535]/15 text-[#FCD535]"><Icon className="w-4 h-4" /></div>
                  <div>
                    <div className="text-white font-semibold text-sm">{r.name}</div>
                    <div className="text-[10px] font-mono uppercase text-[#848E9C]">{r.duration_hours ? `${Math.round(r.duration_hours / 24)}d duration` : 'one-time use'}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[#FCD535] font-mono font-semibold text-sm">{r.cost_points.toFixed(0)}<span className="text-[9px] text-[#848E9C] ml-0.5">pts</span></div>
                </div>
              </div>
              <p className="text-xs text-[#B7BDC6] leading-relaxed min-h-[32px]">{r.description}</p>
              <Button
                onClick={() => redeem(r.reward_id)}
                disabled={!affordable || isActive || redeeming === r.reward_id}
                className={`w-full h-8 text-xs font-semibold ${isActive ? 'bg-[#0ECB81]/20 text-[#0ECB81] hover:bg-[#0ECB81]/20' : 'bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] disabled:bg-[#2B3139] disabled:text-[#5E6673]'}`}
                data-testid={`redeem-${r.reward_id}-btn`}>
                {isActive ? 'Active' : redeeming === r.reward_id ? 'Redeeming…' : affordable ? `Redeem · ${r.cost_points.toFixed(0)} pts` : `Need ${(r.cost_points - balance).toFixed(0)} more pts`}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-[#5E6673] mt-3">
        Points auto-accrue when your referrals sign up. Perks apply to your account automatically.
      </p>

      {/* Season Pass banner */}
      <div className={`mt-6 border p-4 ${seasonActive ? 'border-[#0ECB81]/40 bg-[#0ECB81]/8' : 'border-[#FCD535]/40 bg-gradient-to-r from-[#FCD535]/8 to-transparent'}`} data-testid="season-pass-card">
        <div className="flex items-center gap-3">
          <div className="text-2xl">🎫</div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold">Season Pass{seasonActive ? ' · ACTIVE' : ''}</div>
            <div className="text-xs text-[#B7BDC6]">{seasonActive
              ? `50% off all redemptions until ${new Date(seasonExpiry).toLocaleDateString()}`
              : `Pay ${spCost} pts once → 50% off every reward + bundle for ${spDays} days`}</div>
          </div>
          {!seasonActive && (
            <Button onClick={redeemSeasonPass} disabled={balance < spCost || redeeming === 'season_pass'}
              className="h-9 px-4 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold disabled:bg-[#2B3139] disabled:text-[#5E6673]"
              data-testid="season-pass-redeem-btn">
              {balance >= spCost ? `Activate · ${spCost}p` : `Need ${(spCost - balance).toFixed(0)}p`}
            </Button>
          )}
        </div>
      </div>

      {/* Bundle Deals */}
      {bundles.length > 0 && (
        <div className="mt-6" data-testid="bundles-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs uppercase font-mono text-[#B26BFF]">📦 Bundle Deals</span>
            <span className="text-[10px] text-[#5E6673]">Save 20-23% vs buying separately</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {bundles.map(b => {
              const eff = b.effective_cost;
              const savings = b.base_points - eff;
              const affordable = balance >= eff;
              return (
                <div key={b.bundle_id} className={`border p-3 flex flex-col gap-2 ${affordable ? 'border-[#B26BFF]/40 bg-[#B26BFF]/5 hover:border-[#B26BFF]' : 'border-[#2B3139] bg-[#0B0E11] opacity-70'} transition-colors`} data-testid={`bundle-${b.bundle_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm text-white font-semibold">{b.name}</div>
                    <div className="text-right">
                      <div className="text-[#B26BFF] font-mono text-sm">{eff.toFixed(0)} pts</div>
                      <div className="text-[10px] text-[#5E6673] line-through font-mono">{b.base_points.toFixed(0)} pts</div>
                    </div>
                  </div>
                  <p className="text-[11px] text-[#B7BDC6] leading-snug min-h-[32px]">{b.description}</p>
                  <div className="text-[10px] font-mono text-[#0ECB81]">Save {savings.toFixed(0)} pts</div>
                  <Button
                    onClick={() => redeemBundle(b.bundle_id)}
                    disabled={!affordable || redeeming === `bundle_${b.bundle_id}`}
                    className="w-full h-8 text-xs bg-[#B26BFF] hover:bg-[#9B4EE0] text-white disabled:bg-[#2B3139] disabled:text-[#5E6673]"
                    data-testid={`bundle-redeem-${b.bundle_id}`}>
                    {affordable ? `Redeem · ${eff.toFixed(0)}p` : `Need ${(eff - balance).toFixed(0)}p`}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
